import React, { ReactNode } from 'react';
import { XIcon } from 'lucide-react';
export function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  hint
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  hint?: string;
}) {
  return <label className="block" data-id="element-344" data-mp="4ocioikfrxhf">
      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-345" data-mp="4pdxmy4kp44f">{label}</span>
      <input type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900 outline-none transition-colors focus:border-black" data-id="element-346" data-mp="3d57h9lu7avpp" />
      {hint && <span className="mt-1 block text-xs text-gray-400" data-id="element-347" data-mp="3i2fxhgvsgpob">{hint}</span>}
    </label>;
}
export function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  hint
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  hint?: string;
}) {
  return <label className="block" data-id="element-348" data-mp="uwo9oifxs7fs">
      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-349" data-mp="uvv0lati95uh">{label}</span>
      <textarea rows={rows} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900 outline-none transition-colors focus:border-black" data-id="element-350" data-mp="zoftma4l7ewy" />
      {hint && <span className="mt-1 block text-xs text-gray-400" data-id="element-351" data-mp="bylgrsk9pqcp">{hint}</span>}
    </label>;
}
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm sm:p-6" data-id="element-352" data-mp="1rdiztvg1azc8">
      <div className={`my-6 w-full rounded-3xl bg-white shadow-floating sm:my-10 ${wide ? 'max-w-6xl' : 'max-w-3xl'}`} data-id="element-353" data-mp="1wfqewtw5uvis">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6" data-id="element-354" data-mp="3of6ydpqq8e71">
          <h2 className="font-display text-2xl font-medium text-gray-900" data-id="element-355" data-mp="69k546kiohk5">{title}</h2>
          <button onClick={onClose} aria-label="Stäng" className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200" data-id="element-356" data-mp="67heplfgcmew">
            <XIcon className="h-4 w-4" data-id="element-357" data-mp="34rpqhudbyjii" />
          </button>
        </div>
        <div className="px-8 py-6" data-id="element-358" data-mp="34pcxmm61aoqo">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5" data-id="element-359" data-mp="3jgvml9gvftm6">{footer}</div>}
      </div>
    </div>;
}
export function PrimaryButton({
  children,
  onClick,
  type = 'button'
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
}) {
  return <button type={type} onClick={onClick} className="rounded-full bg-black px-6 py-3 text-sm font-medium text-white transition-transform hover:scale-[1.02]" data-id="element-360" data-mp="2lzwya26352bu">
      {children}
    </button>;
}

/** Toggle chip for multi-select groups (allergens, diets, occasions). */
export function Chip({
  active,
  onClick,
  children,
  tone = 'dark'
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  tone?: 'dark' | 'warn';
}) {
  const on = tone === 'warn' ? 'border-red-600 bg-red-600 text-white' : 'border-black bg-black text-white';
  return <button type="button" aria-pressed={active} onClick={onClick} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-150 ease-out ${active ? on : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'}`} data-id="element-361" data-mp="1p76y2sgqu9jf">
      {children}
    </button>;
}
export function SelectField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
}) {
  return <label className="block" data-id="element-362" data-mp="k3was2l9tdwb">
      <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-363" data-mp="fatf9owxnudh">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full cursor-pointer rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900 outline-none transition-colors focus:border-black" data-id="element-364" data-mp="abgsf462anaw">
        {options.map(o => <option key={o.value} value={o.value} data-id="element-365" data-mp="y1b43iena5tk">
            {o.label}
          </option>)}
      </select>
    </label>;
}
export function FieldLabel({
  children
}: {
  children: ReactNode;
}) {
  return <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-366" data-mp="p1o2avi06xzz">{children}</span>;
}
export function GhostButton({
  children,
  onClick
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return <button type="button" onClick={onClick} className="rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50" data-id="element-367" data-mp="39qkgmpaccqiz">
      {children}
    </button>;
}